/**
 * License Activation Page
 *
 * Allows admins to verify challenge codes from customers
 * and generate activation codes for their desktop apps.
 */
import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  TextField,
  Alert,
  AlertTitle,
  IconButton,
  Tooltip,
  CircularProgress,
  styled,
  alpha,
  Divider,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import KeyIcon from '@mui/icons-material/Key';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import TimerIcon from '@mui/icons-material/Timer';
import axiosInstance from '../../lib/axios';

interface VerifyChallengeResponse {
  success: boolean;
  activationCode?: string;
  activationCodeDisplay?: string;
  expiresAt?: string;
  expiresInHours?: number;
  license?: {
    organizationId: string;
    organizationName: string;
    tier: string;
    maxUsers: number;
    maxLocations: number;
    validUntil: string;
  };
  activations?: {
    current: number;
    max: number;
    remaining: number;
  };
  error?: string;
}

const HeaderBox = styled(Box)(() => ({
  background: 'linear-gradient(135deg, #059669 0%, #0d9488 100%)',
  padding: '48px 40px',
  borderRadius: '16px',
  marginBottom: '32px',
  boxShadow: '0 12px 40px rgba(5, 150, 105, 0.25)',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background:
      'radial-gradient(circle at 20% 50%, rgba(255, 255, 255, 0.1) 0%, transparent 50%), radial-gradient(circle at 80% 80%, rgba(255, 255, 255, 0.08) 0%, transparent 50%)',
  },
}));

const CodeBox = styled(Box)<{ success?: boolean }>(({ success }) => ({
  padding: '20px',
  borderRadius: '12px',
  backgroundColor: success ? alpha('#22c55e', 0.1) : alpha('#7c3aed', 0.05),
  border: `2px solid ${success ? '#22c55e' : alpha('#7c3aed', 0.2)}`,
  fontFamily: 'monospace',
  fontSize: '1.3rem',
  fontWeight: 600,
  letterSpacing: '0.05em',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
}));

const verifyChallenge = async (params: {
  challengeCode: string;
  licenseKey: string;
  signature: string;
}): Promise<VerifyChallengeResponse> => {
  const response = await axiosInstance.post(
    '/licenses/activations/verify',
    params
  );
  return response.data;
};

export const LicenseActivate = () => {
  const navigate = useNavigate();
  const [challengeCode, setChallengeCode] = useState('');
  const [licenseKey, setLicenseKey] = useState('');
  const [signature, setSignature] = useState('');
  const [result, setResult] = useState<VerifyChallengeResponse | null>(null);
  const [copySuccess, setCopySuccess] = useState<string | null>(null);

  const verifyMutation = useMutation({
    mutationFn: verifyChallenge,
    onSuccess: (data) => {
      setResult(data);
    },
  });

  const handleChallengeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Format as CHAL-XXXX-XXXX-XXXX-XXXX
    let value = e.target.value.toUpperCase().replace(/[^A-Z0-9-]/g, '');

    // Add CHAL- prefix if not present
    if (value.length > 0 && !value.startsWith('CHAL')) {
      if (value.startsWith('-')) {
        value = 'CHAL' + value;
      } else {
        value = 'CHAL-' + value;
      }
    }

    // Auto-insert dashes
    const parts = value.replace('CHAL-', '').split('-').join('');
    if (parts.length > 0) {
      const formatted =
        'CHAL-' +
        [
          parts.slice(0, 4),
          parts.slice(4, 8),
          parts.slice(8, 12),
          parts.slice(12, 16),
        ]
          .filter(Boolean)
          .join('-');
      setChallengeCode(formatted);
    } else {
      setChallengeCode(value);
    }
  };

  const handleVerify = () => {
    if (
      challengeCode.match(/^CHAL-[A-Z0-9]{4}-[A-Z0-9]{4}-[A-Z0-9]{4}-[A-Z0-9]{4}$/) &&
      licenseKey.trim() &&
      signature.trim()
    ) {
      verifyMutation.mutate({ challengeCode, licenseKey, signature });
    }
  };

  const handleCopy = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopySuccess(label);
      setTimeout(() => setCopySuccess(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleReset = () => {
    setChallengeCode('');
    setLicenseKey('');
    setSignature('');
    setResult(null);
    verifyMutation.reset();
  };

  const isValidChallengeFormat = challengeCode.match(
    /^CHAL-[A-Z0-9]{4}-[A-Z0-9]{4}-[A-Z0-9]{4}-[A-Z0-9]{4}$/
  );

  return (
    <Box sx={{ pb: 4 }}>
      <HeaderBox>
        <Box sx={{ position: 'relative', zIndex: 1 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/licenses')}
            sx={{ color: 'rgba(255, 255, 255, 0.85)', mb: 2 }}
          >
            Back to Licenses
          </Button>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <VpnKeyIcon sx={{ fontSize: 40, color: '#fff', opacity: 0.9 }} />
            <Typography
              variant="h3"
              sx={{
                color: '#fff',
                fontWeight: 700,
                fontSize: '36px',
                letterSpacing: '-0.02em',
              }}
            >
              Activate Customer License
            </Typography>
          </Box>
          <Typography
            variant="body1"
            sx={{
              color: 'rgba(255, 255, 255, 0.85)',
              fontSize: '15px',
              maxWidth: '600px',
            }}
          >
            Verify a challenge code from a customer to generate their machine-specific
            activation code.
          </Typography>
        </Box>
      </HeaderBox>

      <Card sx={{ borderRadius: '16px', mb: 3 }}>
        <CardContent sx={{ p: 4 }}>
          {!result?.success ? (
            <>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Enter Challenge Code
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                The customer will find this code in their desktop app after entering
                the license key.
              </Typography>

              <TextField
                fullWidth
                label="License Key"
                placeholder="NEXERA-V1-PRO-..."
                value={licenseKey}
                onChange={(e) => setLicenseKey(e.target.value)}
                disabled={verifyMutation.isPending}
                sx={{ mb: 2 }}
              />

              <TextField
                fullWidth
                label="Signature"
                placeholder="Base64-encoded signature"
                value={signature}
                onChange={(e) => setSignature(e.target.value)}
                disabled={verifyMutation.isPending}
                sx={{ mb: 2 }}
              />

              <TextField
                fullWidth
                placeholder="CHAL-XXXX-XXXX-XXXX-XXXX"
                value={challengeCode}
                onChange={handleChallengeChange}
                disabled={verifyMutation.isPending}
                error={challengeCode.length > 5 && !isValidChallengeFormat}
                helperText={
                  challengeCode.length > 5 && !isValidChallengeFormat
                    ? 'Invalid format. Expected: CHAL-XXXX-XXXX-XXXX-XXXX'
                    : ''
                }
                sx={{
                  mb: 3,
                  '& input': {
                    fontFamily: 'monospace',
                    fontSize: '1.2rem',
                    letterSpacing: '0.1em',
                    textAlign: 'center',
                  },
                }}
              />

              {verifyMutation.isError && (
                <Alert severity="error" sx={{ mb: 3 }}>
                  Failed to verify challenge. Please try again.
                </Alert>
              )}

              {result && !result.success && (
                <Alert severity="error" sx={{ mb: 3 }}>
                  {result.error || 'Verification failed'}
                </Alert>
              )}

              <Button
                variant="contained"
                fullWidth
                size="large"
                onClick={handleVerify}
                disabled={
                  !isValidChallengeFormat ||
                  !licenseKey.trim() ||
                  !signature.trim() ||
                  verifyMutation.isPending
                }
                startIcon={
                  verifyMutation.isPending ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    <KeyIcon />
                  )
                }
                sx={{
                  background: 'linear-gradient(135deg, #059669 0%, #0d9488 100%)',
                  borderRadius: '10px',
                  padding: '14px 32px',
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '16px',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #047857 0%, #0f766e 100%)',
                  },
                }}
              >
                {verifyMutation.isPending
                  ? 'Verifying...'
                  : 'Generate Activation Code'}
              </Button>
            </>
          ) : (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                <CheckCircleIcon sx={{ color: '#22c55e', fontSize: 32 }} />
                <Typography variant="h5" fontWeight={600}>
                  Activation Code Generated
                </Typography>
              </Box>

              <Alert severity="success" sx={{ mb: 3 }}>
                <AlertTitle>Success!</AlertTitle>
                Provide this activation code to your customer. They need to enter it
                in their desktop app to complete the activation.
              </Alert>

              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                Activation Code
              </Typography>
              <CodeBox success sx={{ mb: 2 }}>
                <Box sx={{ flex: 1, wordBreak: 'break-all' }}>
                  {result.activationCode}
                </Box>
                <Tooltip
                  title={
                    copySuccess === 'code' ? 'Copied!' : 'Copy activation code'
                  }
                >
                  <IconButton
                    onClick={() => handleCopy(result.activationCode!, 'code')}
                    color={copySuccess === 'code' ? 'success' : 'primary'}
                  >
                    {copySuccess === 'code' ? (
                      <CheckCircleIcon />
                    ) : (
                      <ContentCopyIcon />
                    )}
                  </IconButton>
                </Tooltip>
              </CodeBox>

              {result.activationCodeDisplay && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 3 }}
                >
                  Display code: {result.activationCodeDisplay}
                </Typography>
              )}

              {result.license && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                    License Details
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Tier: {result.license.tier}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Organization: {result.license.organizationName}
                  </Typography>
                </Box>
              )}

              {result.activations && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Activations: {result.activations.current} /{' '}
                  {result.activations.max} used ({result.activations.remaining}{' '}
                  remaining)
                </Typography>
              )}

              <Divider sx={{ my: 3 }} />

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <TimerIcon sx={{ color: 'warning.main' }} />
                <Typography variant="body2" color="text.secondary">
                  Valid for {result.expiresInHours} hours (until{' '}
                  {new Date(result.expiresAt!).toLocaleString()})
                </Typography>
              </Box>

              <Alert severity="warning" sx={{ mb: 3 }}>
                <AlertTitle>Important</AlertTitle>
                <ul style={{ margin: 0, paddingLeft: '20px' }}>
                  <li>
                    This code can only be used on the machine that generated the
                    challenge.
                  </li>
                  <li>The customer must enter it within 24 hours.</li>
                  <li>Once used, the machine is bound to the license.</li>
                </ul>
              </Alert>

              <Button
                variant="outlined"
                fullWidth
                size="large"
                onClick={handleReset}
                sx={{
                  borderRadius: '10px',
                  padding: '12px 32px',
                  textTransform: 'none',
                  fontWeight: 600,
                }}
              >
                Verify Another Challenge
              </Button>
            </>
          )}
        </CardContent>
      </Card>

      {/* Info Card */}
      <Card sx={{ borderRadius: '16px' }}>
        <CardContent sx={{ p: 4 }}>
          <Typography variant="h6" fontWeight={600} gutterBottom>
            How Challenge-Response Activation Works
          </Typography>
          <Box component="ol" sx={{ pl: 2, color: 'text.secondary', m: 0 }}>
            <li>Customer enters their License Key + Signature in the desktop app</li>
            <li>
              Desktop app validates the license and displays a challenge code
              (CHAL-XXXX-...)
            </li>
            <li>Customer sends the challenge code to you (email, phone, chat)</li>
            <li>
              You enter the challenge here and generate an activation code (ACT-...)
            </li>
            <li>You send the activation code back to the customer</li>
            <li>Customer enters the activation code in their desktop app</li>
            <li>License is now bound to that specific machine</li>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default LicenseActivate;
